import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

import {
  Briefcase,
  Flag,
  Flower2,
  Palmtree,
  Thermometer,
  Truck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { isPeriod, matchKnownCategory, type KnownCategory } from '@loggy/shared';

import { Text } from '../design/index.js';
import {
  buildTicks,
  buildValueDomain,
  changeBarSpan,
  formatWeekLabel,
  linearPath,
  resolveTimeRange,
  sortByTime,
  toTime,
} from './chartMath.js';
import { layoutEvents } from './eventLayout.js';
import styles from './MeasurementChart.module.css';
import type { WeeklyChange } from './chartMath.js';
import type { LaidOutEvent } from './eventLayout.js';
import type { MeasurementChartProps, SeriesPoint } from './types.js';

export type { MeasurementChartProps, SeriesPoint } from './types.js';

/**
 * The logical coordinate system the SVG is drawn in. The height is in real
 * pixels and already includes the x-axis label band, so the container never
 * needs a separate fixed-height wrapper that clips the axis into a nested
 * scrollbar.
 *
 * The WIDTH, unlike the height, tracks the container's actual measured
 * pixel width (see the ResizeObserver below) rather than being a fixed
 * viewBox stretched to fit via `width: 100%`. A chart that only stretched
 * would also shrink every piece of embedded SVG text by the same ratio —
 * fine on a 948px desktop card, illegible at 360px, where an 11px tick
 * label would render at under 6px. Keeping viewBox width equal to the
 * measured CSS width keeps 1 user unit = 1 real pixel, so text stays
 * true-to-size at any container width.
 */
/**
 * How tall the chart draws, given how wide it ended up and how tall the
 * screen is.
 *
 * Deliberately neither a fixed height nor a fixed ceiling. A flat 220 was
 * fine while a column cap kept the chart near 1200px; without that cap it
 * becomes a ribbon, and the line flattens towards horizontal until the shape
 * — the entire reason to draw a line rather than print the numbers — stops
 * being readable. A ceiling instead of a cap only moves the problem: at
 * 3440px a 440 ceiling still gives 6.7:1.
 *
 * So the chart holds a PROPORTION, and the screen's own height is what
 * bounds it. Two honest constraints and no magic number for "tall":
 *
 *   - keep TARGET_ASPECT, so the shape reads the same at any width;
 *   - never take more than VIEWPORT_SHARE of the window, so the readings and
 *     the goal underneath stay reachable without scrolling past a chart.
 *
 * On a tall display the proportion wins and the chart simply gets bigger. On
 * a short wide one the share wins, which is the right way round: a laptop in
 * a 3440px-wide window has the pixels but not the room.
 */
const MIN_HEIGHT = 220;
const TARGET_ASPECT = 3.2;
const VIEWPORT_SHARE = 0.55;
const DEFAULT_WIDTH = 640;
/** Used only before a real window has been measured. */
const DEFAULT_VIEWPORT_HEIGHT = 800;

function heightForWidth(width: number, viewportHeight: number): number {
  const proportional = width / TARGET_ASPECT;
  const roomOnScreen = viewportHeight * VIEWPORT_SHARE;
  return Math.round(Math.max(MIN_HEIGHT, Math.min(proportional, roomOnScreen)));
}

/**
 * The window's height, kept live.
 *
 * `globalThis` rather than the bare `window`, and that is not a stylistic
 * choice: this component takes a PROP called `window` (the time span it
 * draws), which shadows the global inside it. Writing `window.innerHeight`
 * here reads the prop and fails.
 */
function useViewportHeight(): number {
  const [height, setHeight] = useState(() =>
    typeof globalThis.innerHeight === 'number' ? globalThis.innerHeight : DEFAULT_VIEWPORT_HEIGHT,
  );
  useEffect(() => {
    const handler = () => setHeight(globalThis.innerHeight);
    globalThis.addEventListener('resize', handler);
    return () => globalThis.removeEventListener('resize', handler);
  }, []);
  return height;
}

/**
 * Margins are plain geometry, not design tokens — there is no `--radius`- or
 * `--space`-shaped scale for "how many px an axis label band needs", the same
 * reasoning the design README gives for the primitives' own un-tokened
 * pixel literals (a 1px hairline, a 44px touch target).
 */
const MARGIN = { top: 16, right: 46, bottom: 28, left: 38 };

const TICK_COUNT = 5;

/**
 * How far a touch drag has to travel, cumulatively, before it commits to
 * panning rather than staying a tap-to-read. Close to the touch-slop figures
 * both major platforms already use for "is this a tap or a drag" (Android's
 * default ~8dp, iOS's UIPanGestureRecognizer ~10pt) — small enough that a
 * deliberate drag starts panning almost immediately, large enough that a
 * finger that trembles slightly while tapping still reads a value.
 */


/**
 * The change strip beneath the line: a fixed extra height plus a gap, added
 * to the SVG only when `changes` is present and non-empty. When absent the
 * SVG is exactly the frame's own height.
 */
const STRIP_HEIGHT = 64;
const STRIP_GAP = 16;
const STRIP_BAR_MAX_WIDTH = 24;
const STRIP_BAR_GAP = 2;
const STRIP_BAR_RADIUS = 4;
/** So a flat week reads as flat, not as missing. */
const MIN_BAR_HEIGHT = 1.5;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * How many pixels one notch of a line-mode wheel is taken to be.
 *
 * A trackpad reports pixels (`deltaMode` 0) and needs none of this. A mouse
 * wheel with a horizontal tilt reports LINES, and a line is a font metric the
 * chart has no business guessing precisely — 16 is the figure browsers
 * themselves use for a default line box, and being a little out only makes
 * the pan slightly faster or slower, never wrong.
 */
const WHEEL_LINE_PX = 16;

/**
 * The events lane above the plot: its own reserved rows, added only when
 * `events` actually lays out to something, so a chart with no events keeps
 * exactly the geometry it had before this lane existed. No title is ever
 * drawn inside the plot area — this is the room the plot's own geometry
 * makes for it, not an overlay drawn on top.
 */
/**
 * Roughly how tall a tooltip gets. Below this much room above a reading it
 * looks for somewhere better to go.
 *
 * Having too little room above is not on its own a reason to go down — a
 * reading halfway down a short plot has too little room on BOTH sides, and
 * flipping it there only moves where it gets cut. So the side with more room
 * wins, and `max-block-size` in the stylesheet catches the case where neither
 * side is enough.
 */
const TOOLTIP_FLIP_HEADROOM = 96;

const EVENT_LABEL_ROW_HEIGHT = 16;
/**
 * How many rows of labels the lane holds, and so how much room it reserves.
 *
 * Two, because the lane's height has to be a constant — see `eventsLaneHeight`
 * — and a constant has to be chosen. Two covers every window this catalogue of
 * events produces at any ordinary zoom; a third would cost every chart the
 * room permanently to serve a case that is rare. An event that cannot be given
 * a row keeps its mark on the plot and loses only its title.
 */
const MAX_EVENT_ROWS = 2;
const EVENT_LANE_GAP = 8;
const EVENT_LABEL_BASELINE_PAD = 4;

/**
 * Reserved before every label's own text for its category icon (or the
 * generic mark drawn when it has none) — handed to `layoutEvents` so the
 * icon is part of the row-packing width budget, never added afterwards.
 * Plain geometry, the same reasoning MARGIN above gets: no token scale for
 * "how wide is a chart's own tiny glyph".
 */
const EVENT_ICON_WIDTH_PX = 14;
const EVENT_ICON_GLYPH_SIZE = 6;
/** The drawn size of a category glyph, inside the 14px slot layoutEvents reserves. */
const EVENT_ICON_DRAWN_SIZE = 11;

/**
 * Touch-target.dsds.yaml's 44px floor, applied to the events lane only
 * under a coarse pointer. Not `var(--touch-target)` — this sizes plotted
 * SVG geometry from JS, where a CSS custom property cannot reach — but it
 * is the same figure the token holds, so a row grows to fit the touch
 * target rather than the touch target overflowing a fixed row. The lane
 * itself gets taller to make room; the plot below it never shrinks.
 */
const EVENT_ROW_HEIGHT_COARSE = 44;

/** One thing can be active at a time — never a reading and an event
 *  together. See the interaction handlers below for how each control
 *  overwrites this on its own hover/focus and only clears the piece of it
 *  that is its own, so a leave/blur on one control can never erase what a
 *  different control just made active. */
type Active = { kind: 'reading'; index: number } | { kind: 'event'; id: string } | null;

/** Whether the pointer is coarse (touch) right now, kept live via
 *  `matchMedia` rather than read once — the same condition
 *  `@media (pointer: coarse)` tests in CSS, mirrored here because the
 *  events lane's row height is plotted SVG geometry, not something CSS
 *  alone can resize. */
function useCoarsePointer(): boolean {
  const [isCoarse, setIsCoarse] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false,
  );
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(pointer: coarse)');
    const handler = () => setIsCoarse(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isCoarse;
}

const SOURCE_LABEL: Record<NonNullable<SeriesPoint['source']>, string> = {
  manual: 'Entered by hand',
  withings: 'Withings',
  import: 'Imported',
};

interface Plotted {
  point: SeriesPoint;
  t: number;
  x: number;
  y: number;
  yMin?: number;
  yMax?: number;
}

function formatValue(value: number, precision: number): string {
  return value.toFixed(Math.max(0, precision));
}

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const axisFormatters = {
  withYear: new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' }),
  monthOnly: new Intl.DateTimeFormat('en-GB', { month: 'short' }),
  dayMonth: new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }),
};

function formatAxisTick(t: number, spanMs: number): string {
  const date = new Date(t);
  const YEAR = 365 * 24 * 60 * 60 * 1000;
  const MONTH = 30 * 24 * 60 * 60 * 1000;
  if (spanMs > YEAR) return axisFormatters.withYear.format(date);
  if (spanMs > MONTH * 2) return axisFormatters.monthOnly.format(date);
  return axisFormatters.dayMonth.format(date);
}

function formatTickValue(value: number, step: number): string {
  const decimals = step < 1 ? 1 : 0;
  return value.toFixed(decimals);
}

/** "0.4 kg lost this week", "0.2 kg gained this week", "no change this
 *  week" — and "so far" in place of a completed week's phrasing while
 *  `inProgress` is true, so a partial week is never read as a finished one. */
/**
 * Which days a week's change was actually measured between.
 *
 * The bar's own position says which week it belongs to, and that is not the
 * same question: a change measured from Monday to Sunday and one measured
 * from Friday to Saturday are both "this week", and they mean very different
 * things about what happened. Only the days the scale was actually stood on
 * can say which — so the readout names them rather than implying the week
 * was covered end to end.
 *
 * The word "measured" is load-bearing. Without it the readout was
 * "week 38 · 8 to 10 Sept", and the owner read that as the week itself being
 * three days long and asked whether the bucketing was broken. It is not — the
 * server buckets Monday to Monday in UTC — but a label that has to be
 * defended is a label that is wrong, and one word says which question the
 * dates are answering.
 */
function formatChangeSpan(change: WeeklyChange): string {
  const from = new Date(change.firstAt);
  const to = new Date(change.lastAt);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
  const sameDay = from.toDateString() === to.toDateString();
  // Both readings on one day is a real case — two weigh-ins that morning —
  // and "8 Sept to 8 Sept" reads as a mistake rather than a fact.
  if (sameDay) return `measured on ${changeSpanFormatter.format(from)}`;
  return `measured ${changeSpanFormatter.format(from)} to ${changeSpanFormatter.format(to)}`;
}

const changeSpanFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const weekEndFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
/** Monday to Sunday: six days past the bucket's own start, not seven. */
const WEEK_LAST_DAY_MS = 6 * 24 * 60 * 60 * 1000;

function formatChangeText(change: WeeklyChange, unit: string, precision: number): string {
  const timing = change.inProgress ? 'so far this week' : 'this week';
  // The label names the week; the dates say which of its days were actually
  // measured. Both, because either alone leaves a real question open.
  const span = [formatWeekLabel(change.weekStart) ?? '', formatChangeSpan(change)]
    .filter(Boolean)
    .join(' · ');
  const suffix = span ? ` (${span})` : '';
  if (change.change === 0) return `no change ${timing}${suffix}`;
  const magnitude = formatValue(Math.abs(change.change), precision);
  const verb = change.change > 0 ? 'gained' : 'lost';
  return `${magnitude} ${unit} ${verb} ${timing}${suffix}`;
}

/**
 * A bar rounded on the DATA end only — the corner away from zero — and
 * square where it meets the zero baseline, so the baseline itself reads as
 * one continuous edge rather than being interrupted by every bar's corner.
 */
function changeBarPath(x: number, top: number, width: number, height: number, roundTop: boolean): string {
  if (height <= 0) return '';
  const r = Math.min(STRIP_BAR_RADIUS, width / 2, height);
  if (roundTop) {
    return `M ${x} ${top + height} L ${x} ${top + r} Q ${x} ${top} ${x + r} ${top} L ${x + width - r} ${top} Q ${x + width} ${top} ${x + width} ${top + r} L ${x + width} ${top + height} Z`;
  }
  return `M ${x} ${top} L ${x + width} ${top} L ${x + width} ${top + height - r} Q ${x + width} ${top + height} ${x + width - r} ${top + height} L ${x + r} ${top + height} Q ${x} ${top + height} ${x} ${top + height - r} L ${x} ${top} Z`;
}

/**
 * The mark drawn before an event's title, inside the icon slot `layoutEvents`
 * already reserved.
 *
 * Never a colour. The marks stay neutral so they cannot compete with the
 * measurement line — the whole reason this layer draws behind the series —
 * and the accent, status and identity scales are each already spoken for.
 * Shape is what distinguishes one category from another, which is also the
 * only distinction that survives being colour-blind or printed.
 *
 * A category the app recognises gets its own glyph; a word of the owner's
 * own gets a plain dot, which is honest — the app has no idea what a
 * renovation looks like and should not pretend otherwise.
 */
const CATEGORY_ICONS: Record<KnownCategory, LucideIcon> = {
  Holiday: Palmtree,
  // A flower rather than a broken heart. This mark sits above somebody's
  // chart on an ordinary Tuesday, and it should not shout at them.
  Loss: Flower2,
  Illness: Thermometer,
  Move: Truck,
  Work: Briefcase,
  Relationship: Users,
  Milestone: Flag,
};

function EventIcon({ category, cx, cy }: { category: string | null; cx: number; cy: number }) {
  const known = matchKnownCategory(category);
  if (known === null) {
    return (
      <circle
        aria-hidden="true"
        className={styles.eventIconGeneric}
        cx={cx}
        cy={cy}
        r={EVENT_ICON_GLYPH_SIZE / 2 - 1}
      />
    );
  }

  const Icon = CATEGORY_ICONS[known];
  const half = EVENT_ICON_DRAWN_SIZE / 2;
  return (
    // Translated rather than given x/y: a nested <svg> positions differently
    // across engines, and a <g> transform is the one that behaves the same
    // everywhere in this chart's own coordinate space.
    <g transform={`translate(${cx - half} ${cy - half})`} className={styles.eventIconKnown} aria-hidden="true">
      <Icon size={EVENT_ICON_DRAWN_SIZE} strokeWidth={2} />
    </g>
  );
}

/**
 * A pure, presentational hand-written SVG chart. It takes data as props,
 * fetches nothing, and imports nothing from `web/src/api.ts`.
 *
 * The x position of every point is derived from `recordedAt` (via
 * `chartMath`'s `toTime`/`sortByTime`) and mapped through a linear time
 * scale below — never from the point's index in the array, which would
 * silently compress the real month-long gaps in seven years of history into
 * even steps.
 */
export function MeasurementChart({
  series,
  unit,
  precision,
  aggregated,
  bucket,
  goalThreshold,
  suggestedAxis,
  valueAxis,
  changes,
  events,
  window,
  onPan,
}: MeasurementChartProps) {
  const gradientId = useId();
  const plotClipId = useId();
  const popoverId = useId();
  const frameRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const viewportHeight = useViewportHeight();
  const [availableHeight, setAvailableHeight] = useState(0);
  // ONE piece of state drives every popover — see the `Active` type above.
  // Each interaction handler below sets it directly rather than the two
  // separate hover/focus indices this replaced: an event is not a point in
  // `plotted`, and hoverIndex ?? focusIndex could never have named which of
  // a reading and an event was the most recent interaction when the two
  // controls disagreed (a pointer left resting on the plot while the owner
  // Tabs to an event label).
  const [active, setActive] = useState<Active>(null);
  // Which event label currently holds the lane's one roving tab stop — see
  // the "roving tabindex" comment on the events lane below. Independent of
  // `active`: the roving stop can sit on a label that pointer-hover has
  // since moved the popover away from.
  const [rovingEventIndex, setRovingEventIndex] = useState(0);
  // Whether the reading slider / an event label currently holds real DOM
  // focus — not exposed as render state, only consulted by a pointer-leave
  // handler so that leaving with the mouse never hides a popover that a
  // keyboard user is still legitimately focused on. Mirrors exactly what
  // the slider already did before this change (focusIndex survived a
  // pointer leave); extended here to the event labels for the same reason.
  const readingFocusedRef = useRef(false);
  const focusedEventIdRef = useRef<string | null>(null);
  const eventHitRefs = useRef(new Map<string, SVGRectElement>());
  const isCoarsePointer = useCoarsePointer();
  const eventRowHeightPx = isCoarsePointer ? EVENT_ROW_HEIGHT_COARSE : EVENT_LABEL_ROW_HEIGHT;

  // Keeps the viewBox's width equal to the container's real CSS width — see
  // the comment on MIN_HEIGHT/DEFAULT_WIDTH above for why.
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setWidth(Math.max(1, Math.round(entry.contentRect.width)));
      // The HEIGHT the layout is prepared to give this chart, which is not
      // the same question as how tall it would like to be. See `frameHeight`.
      setAvailableHeight(Math.max(0, Math.round(entry.contentRect.height)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /**
   * A horizontal trackpad swipe over the chart moves it through time.
   *
   * This is the gesture that went missing, and it is worth saying exactly how.
   * The chart used to sit INSIDE the horizontal scroll container, so swiping
   * over it scrolled that container and the browser did all of this for free.
   * Taking the chart out — which had to happen, because a scroll container
   * clips on BOTH axes and was cutting the tooltips — left the scroller a
   * twelve-pixel strip beside the chart, somewhere a cursor never is. The
   * scrolling still worked; there was just nothing under the pointer to
   * scroll. So the gesture comes back as a listener, and deliberately NOT as
   * a scroll container: `.frame` has no `overflow` at all, which is the whole
   * reason the tooltips are no longer cut, and nothing here adds one.
   *
   * Attached by hand rather than with `onWheel`, because React registers
   * wheel listeners at the root as PASSIVE — `preventDefault` inside a React
   * `onWheel` is a no-op that only logs a warning, so the browser would go on
   * treating the swipe as a back-navigation at the same time.
   *
   * A vertical swipe is never claimed: the page has to keep scrolling under a
   * chart that is taller than some phones.
   */
  const panState = useRef<{ onPan?: (deltaDays: number) => void; msPerPx: number }>({ msPerPx: 0 });

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    function handleWheel(event: WheelEvent) {
      const { onPan: pan, msPerPx } = panState.current;
      if (!pan || msPerPx <= 0) return;
      // Dominantly horizontal, or it belongs to the page.
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      const px = event.deltaMode === 0 ? event.deltaX : event.deltaX * WHEEL_LINE_PX;
      pan((px * msPerPx) / DAY_MS);
    }
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  // Only the x-bounds are fixed by the container's width; the y-bounds are
  // not known until the events lane below has claimed its own rows, so they
  // are computed in two steps rather than one.
  const plotX = useMemo(() => ({ x0: MARGIN.left, x1: width - MARGIN.right }), [width]);
  /**
   * How tall to draw, given both what the chart WANTS and what the layout is
   * actually handing it.
   *
   * `heightForWidth` is the want: a proportion of the width, bounded by the
   * window. That was the whole story while every chart card was exactly as
   * tall as its contents. Then the cards in the row were made to end level
   * with each other, and the shorter one — this one — got a taller box with
   * the same drawing inside it and a band of empty card underneath. The
   * owner saw it immediately: "the graph itself is not taking advantage of
   * the extra space, it's just empty space."
   *
   * So the measured height wins when it is the larger of the two. No loop:
   * `.frame` is a flex child with `flex: 1`, so its height comes from the
   * card rather than from the SVG inside it — when the card is content-sized
   * the two agree and nothing moves, and when the card is stretched the
   * chart grows into the difference.
   *
   * The strip, when there is one, is drawn BELOW the frame's own height and
   * has to come out of the budget first, or the chart would overflow its box
   * by exactly the strip.
   */
  const stripAllowance = (changes?.length ?? 0) > 0 ? STRIP_GAP + STRIP_HEIGHT : 0;
  const frameHeight = Math.max(
    heightForWidth(width, viewportHeight),
    availableHeight > 0 ? availableHeight - stripAllowance : 0,
  );

  const sorted = useMemo(() => sortByTime(series), [series]);

  // The x-domain events are laid out against — deliberately the SAME range
  // xScale below uses (the explicit `window`, when the screen gave one,
  // otherwise the series' own first and last point — see
  // `resolveTimeRange`), so an event lines up with the reading beside it.
  // Computed separately from the big memo below (which derives the same
  // range again for the series itself) so the lane's height is known before
  // that memo needs a final plot.y0/y1 to place points with.
  const eventsTimeRange = useMemo<[number, number]>(
    () => resolveTimeRange(sorted, window),
    [sorted, window],
  );

  const eventsLayout = useMemo<LaidOutEvent[]>(() => {
    if (!events || events.length === 0 || sorted.length === 0) return [];
    if (plotX.x1 <= plotX.x0) return [];
    const [minT, maxT] = eventsTimeRange;
    return layoutEvents(
      events,
      { from: new Date(minT).toISOString(), to: new Date(maxT).toISOString() },
      plotX,
      {
        iconWidthPx: EVENT_ICON_WIDTH_PX,
        rowHeightPx: eventRowHeightPx,
        // The 44px touch-target floor only under a coarse pointer — see
        // touch-target.dsds.yaml's own "not unconditionally" guideline; a
        // dense desktop layout keeps its natural, usually-narrower widths.
        minHitWidthPx: isCoarsePointer ? EVENT_ROW_HEIGHT_COARSE : 0,
        maxRows: MAX_EVENT_ROWS,
      },
    );
  }, [events, sorted.length, eventsTimeRange, plotX, eventRowHeightPx, isCoarsePointer]);

  // Reserved rows above the plot, one per stacked label — zero, and so
  // byte-for-byte the same geometry as before this lane existed, whenever
  // `events` is absent or lays out to nothing. Each row is `eventRowHeightPx`
  // tall rather than the fixed EVENT_LABEL_ROW_HEIGHT: under a coarse
  // pointer the lane grows to fit a 44px hit target per row, so the chart
  // gets taller on a phone rather than a hit box reaching into the plot.
  /**
   * Fixed while the layer is on, rather than sized to whatever happens to be
   * in the frame.
   *
   * It used to be one row per stacked label, counted from the events actually
   * laid out. That made the chart taller in stretches of history with several
   * events and shorter where there were none — so scrolling back through the
   * years made the whole page jump every time a label came into view or left
   * it. Reserving the space costs a little room when nothing is there and is
   * worth it: the reader is reading a line, and the line should not move under
   * them because of something arriving beside it.
   *
   * `events` being undefined — no events node in this dashboard at all —
   * reserves nothing, so a chart that can never have the layer is
   * byte-for-byte what it always was. An events node that exists with the
   * layer switched off passes an empty array instead, which reserves the lane
   * without drawing in it, so ticking the box fills space that was already
   * there.
   */
  const eventsLaneHeight = events === undefined ? 0 : MAX_EVENT_ROWS * eventRowHeightPx + EVENT_LANE_GAP;

  // Centres the label's own (fixed-size) text vertically inside its row,
  // whatever that row's height is — at EVENT_LABEL_ROW_HEIGHT this reduces
  // to exactly the original row*16+16-4 baseline, since 16/2+4 = 12 = 16-4.
  function eventLabelBaselineY(row: number): number {
    return MARGIN.top + row * eventRowHeightPx + eventRowHeightPx / 2 + EVENT_LABEL_BASELINE_PAD;
  }

  const plot = useMemo(
    () => ({
      x0: plotX.x0,
      x1: plotX.x1,
      // The lane is taken OUT of the chart's own height rather than added to
      // it: the plot loses a little room, the box around it never changes
      // size. Added, turning the layer on grew the whole chart and shoved
      // everything below it down the page — which is a jump in response to a
      // tick box, and a tick box should not move the page.
      y0: MARGIN.top + eventsLaneHeight,
      y1: frameHeight - MARGIN.bottom,
    }),
    [plotX, eventsLaneHeight, frameHeight],
  );

  const { plotted, valueTicks, timeRange, yScale, xScale, goalOnAxis } = useMemo(() => {
    // The x-domain: the explicit `window` when the screen gave one,
    // otherwise the data's own first and last point — see
    // `resolveTimeRange`'s own doc comment for why an auto-derived range
    // silently lies the moment a window is sparse. Computed BEFORE the
    // `sorted.length === 0` branch below, and used by it too, so a window
    // holding no points at all still draws a real axis instead of
    // collapsing to a placeholder [0, 1].
    const [minT, maxT] = resolveTimeRange(sorted, window);
    const xScale = (t: number) =>
      minT === maxT ? (plot.x0 + plot.x1) / 2 : plot.x0 + ((t - minT) / (maxT - minT)) * (plot.x1 - plot.x0);

    if (sorted.length === 0) {
      // No points to draw, but the axis itself is still real: the y-domain
      // falls back to whatever `suggestedAxis`/`goalThreshold` alone can
      // give it (buildValueDomain's own [0, 1] placeholder when neither is
      // present), and the x-domain is the window computed above — never the
      // [0, 1] this branch used to hand back regardless of `window`.
      const domain = buildValueDomain({ values: [], precision, suggestedAxis, goalThreshold });
      const ticks = buildTicks(domain, TICK_COUNT);
      const yScale = (v: number) =>
        domain.max === domain.min
          ? (plot.y0 + plot.y1) / 2
          : plot.y1 - ((v - domain.min) / (domain.max - domain.min)) * (plot.y1 - plot.y0);
      return {
        plotted: [] as Plotted[],
        valueTicks: ticks,
        timeRange: [minT, maxT] as [number, number],
        yScale,
        xScale,
        goalOnAxis: false,
      };
    }

    const times = sorted.map((p) => toTime(p));

    const allValues = sorted.flatMap((p) =>
      p.min !== undefined && p.max !== undefined ? [p.min, p.max, p.value] : [p.value],
    );
    // `valueAxis` is an instruction, `suggestedAxis` is a floor. When the
    // caller has decided the axis, the readings do not get to widen it —
    // which is the entire difference between a chart you can zoom vertically
    // and one that always rescales to whatever it was handed.
    const domain = valueAxis ?? buildValueDomain({ values: allValues, precision, suggestedAxis, goalThreshold });
    const ticks = buildTicks(domain, TICK_COUNT);
    const firstTick = ticks[0] ?? domain.min;
    const lastTick = ticks[ticks.length - 1] ?? domain.max;
    // Union the plotting scale with the ticks so a rounded gridline is never
    // clipped just outside the padded data domain.
    const finalDomain = { min: Math.min(domain.min, firstTick), max: Math.max(domain.max, lastTick) };

    const yScale = (v: number) =>
      finalDomain.max === finalDomain.min
        ? (plot.y0 + plot.y1) / 2
        : plot.y1 - ((v - finalDomain.min) / (finalDomain.max - finalDomain.min)) * (plot.y1 - plot.y0);

    const nextPlotted: Plotted[] = sorted.map((point, i) => ({
      point,
      t: times[i] ?? minT,
      x: xScale(times[i] ?? minT),
      y: yScale(point.value),
      yMin: point.min !== undefined ? yScale(point.min) : undefined,
      yMax: point.max !== undefined ? yScale(point.max) : undefined,
    }));

    // Whether the goal fell inside the axis the readings earned. When it did
    // not, `buildValueDomain` deliberately left it out rather than flatten
    // them, and the chart marks it at the edge instead of drawing a line
    // somewhere off the plot.
    const goalOnAxis =
      goalThreshold !== undefined &&
      goalThreshold >= finalDomain.min &&
      goalThreshold <= finalDomain.max;

    return {
      plotted: nextPlotted,
      valueTicks: ticks,
      timeRange: [minT, maxT] as [number, number],
      yScale,
      xScale,
      goalOnAxis,
    };
  }, [sorted, aggregated, precision, suggestedAxis, valueAxis, goalThreshold, plot, window]);

  // Only ever set from an interaction with the reading slider itself, never
  // from one with an event label — so `aria-valuenow`/`aria-valuetext`
  // below fall back to the last reading exactly as they did before an
  // event could be the thing active, rather than describing an event.
  const activeReadingIndex = active?.kind === 'reading' ? active.index : null;
  const activePoint = activeReadingIndex !== null ? plotted[activeReadingIndex] : undefined;
  const activeEvent = active?.kind === 'event' ? eventsLayout.find((e) => e.event.id === active.id) : undefined;
  /**
   * The last reading actually on screen.
   *
   * Not simply the last of `plotted`: the series arrives with one reading
   * either side of the window so the line can cross both edges, and the
   * trailing one is outside the plot and clipped away. Labelling that one
   * would put the current value off the edge of the chart, where the clip
   * then eats it.
   */
  const last = [...plotted].reverse().find((p) => p.x <= plot.x1 + 0.5) ?? plotted[plotted.length - 1];
  const tickStep = valueTicks.length > 1 ? (valueTicks[1] ?? 0) - (valueTicks[0] ?? 0) : 1;
  const [minT, maxT] = timeRange;

  // Read by the wheel listener above, which is attached once and so cannot
  // close over either of these. How wide a day currently is, in pixels, is
  // exactly what turns a swipe into a number of days.
  panState.current = {
    onPan,
    msPerPx: plotX.x1 > plotX.x0 ? (maxT - minT) / (plotX.x1 - plotX.x0) : 0,
  };
  const spanMs = maxT - minT;

  // `changes` is produced per week, not per plotted point, and `weeklyChanges`
  // SKIPS a week that lacks the data to compute one — so it can be shorter
  // than `plotted` and is never index-aligned with it. Every lookup below
  // goes through this map, keyed by `weekStart`, which is exactly the
  // `recordedAt` of the weekly bucket it was computed from — never by
  // position.
  const changesByWeek = useMemo(() => {
    const map = new Map<string, WeeklyChange>();
    for (const change of changes ?? []) map.set(change.weekStart, change);
    return map;
  }, [changes]);

  const hasChanges = (changes?.length ?? 0) > 0;

  interface ChangeBar {
    change: WeeklyChange;
    x: number;
    width: number;
    barTop: number;
    barHeight: number;
    roundTop: boolean;
  }

  // The strip's own layout: its own value domain (forced to include 0), its
  // own scale, and its own band beneath the main plot. It NEVER shares the
  // main yScale — a level in `unit` and a change in `unit` are different
  // measures, and one vertical scale for both is the single worst thing a
  // chart can do.
  const stripLayout = useMemo(() => {
    if (!hasChanges) return null;
    const values = (changes ?? []).map((c) => c.change);
    // suggestedAxis seeds the scan at 0..0, so the resulting domain always
    // spans zero even when every change in the window has the same sign —
    // a month of steady losses still has to read against a real zero, not
    // an axis that quietly starts at the least negative week.
    const domain = buildValueDomain({ values, precision, suggestedAxis: { min: 0, max: 0 } });
    const stripY0 = plot.y1 + STRIP_GAP;
    const stripY1 = stripY0 + STRIP_HEIGHT;
    const stripScale = (v: number) =>
      domain.max === domain.min
        ? (stripY0 + stripY1) / 2
        : stripY1 - ((v - domain.min) / (domain.max - domain.min)) * (stripY1 - stripY0);
    const zeroY = stripScale(0);

    // Bar width comes from the axis, not a guess: the pixel distance one
    // week actually spans at the current zoom, capped so a short history
    // doesn't draw a single week as a wall.
    const pxPerMs = maxT > minT ? (plot.x1 - plot.x0) / (maxT - minT) : 0;
    const pixelsPerWeek = pxPerMs * WEEK_MS;

    const bars: ChangeBar[] = (changes ?? []).map((change) => {
      const centerX = xScale(toTime({ recordedAt: change.weekStart }));
      // Centred on the point it belongs to — see changeBarSpan for why that
      // is not cosmetic.
      const { left, width } = changeBarSpan(centerX, pixelsPerWeek, STRIP_BAR_MAX_WIDTH, STRIP_BAR_GAP);
      const y = stripScale(change.change);
      const roundTop = change.change >= 0;
      const trueHeight = Math.abs(y - zeroY);
      // A week that genuinely did not move still gets a visible mark: a bar of
      // no height at all is indistinguishable from a week we had no reading
      // for, and "flat" and "unknown" are different claims. The stub straddles
      // the baseline rather than hanging below it, which would read as a small
      // loss the person never had.
      const barHeight = Math.max(trueHeight, MIN_BAR_HEIGHT);
      const barTop =
        trueHeight < MIN_BAR_HEIGHT ? zeroY - MIN_BAR_HEIGHT / 2 : Math.min(y, zeroY);
      return { change, x: left, width, barTop, barHeight, roundTop };
    });

    return { stripY0, stripY1, zeroY, bars };
  }, [hasChanges, changes, precision, plot, minT, maxT, xScale]);

  // The whole SVG's height: the frame's own height when there is no strip.
  // With a strip, the axis (and the x-axis date labels, which
  // label both bands alike) move down to sit beneath it.
  const axisBottomY = stripLayout ? stripLayout.stripY1 : plot.y1;
  const svgHeight = axisBottomY + MARGIN.bottom;

  function nearestIndexForClientX(clientX: number): number | null {
    const svg = svgRef.current;
    if (!svg || plotted.length === 0) return null;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0) return null;
    const viewX = ((clientX - rect.left) / rect.width) * width;
    let bestIndex = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < plotted.length; i++) {
      const candidate = plotted[i];
      if (!candidate) continue;
      const distance = Math.abs(candidate.x - viewX);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }
    return bestIndex;
  }

  // The reading nearest an event's own VISIBLE start (see
  // `LaidOutEvent.visibleStartMs`), by time rather than by pixel — so a
  // period clipped at the chart's left edge is measured against where it is
  // actually drawn, never against a real start that predates every reading
  // on screen.
  function nearestPlottedTo(t: number): Plotted | undefined {
    if (plotted.length === 0) return undefined;
    let best: Plotted | undefined;
    let bestDistance = Infinity;
    for (const candidate of plotted) {
      const distance = Math.abs(candidate.t - t);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    }
    return best;
  }

  function handlePointerMove(event: PointerEvent<SVGRectElement>) {
    const index = nearestIndexForClientX(event.clientX);
    if (index !== null) setActive({ kind: 'reading', index });
  }

  function handlePointerLeave() {
    // A pointer leaving the plot never erases an event's popover — it was
    // never this control's to clear — and never erases a reading the owner
    // is still legitimately keyboard-focused on.
    setActive((current) => (current?.kind === 'reading' && !readingFocusedRef.current ? null : current));
  }

  function handleReadingFocus() {
    readingFocusedRef.current = true;
    setActive((current) => (current?.kind === 'reading' ? current : { kind: 'reading', index: plotted.length - 1 }));
  }

  function handleReadingBlur() {
    readingFocusedRef.current = false;
    setActive((current) => (current?.kind === 'reading' ? null : current));
  }

  function handleKeyDown(event: KeyboardEvent<SVGRectElement>) {
    if (plotted.length === 0) return;
    const current = activeReadingIndex ?? plotted.length - 1;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setActive({ kind: 'reading', index: Math.min(plotted.length - 1, current + 1) });
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setActive({ kind: 'reading', index: Math.max(0, current - 1) });
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive({ kind: 'reading', index: 0 });
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive({ kind: 'reading', index: plotted.length - 1 });
    }
  }

  // Roving tabindex over the events lane: the lane is ONE tab stop, not one
  // per event. `rovingEventIndex` names which label currently carries
  // tabIndex=0; arrow keys move it (and move real DOM focus with it) in
  // time order, exactly the idiom the reading slider above already uses for
  // its own values — Home/End jump to the first/last label the same way
  // they jump to the first/last reading. Tab, untouched here, leaves the
  // lane in a single press because only one label is ever in tab order.
  function handleEventKeyDown(event: KeyboardEvent<SVGRectElement>, index: number) {
    if (eventsLayout.length === 0) return;
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = Math.min(eventsLayout.length - 1, index + 1);
    else if (event.key === 'ArrowLeft') nextIndex = Math.max(0, index - 1);
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = eventsLayout.length - 1;
    if (nextIndex === null || nextIndex === index) return;
    event.preventDefault();
    const nextEvent = eventsLayout[nextIndex];
    if (!nextEvent) return;
    setRovingEventIndex(nextIndex);
    setActive({ kind: 'event', id: nextEvent.event.id });
    focusedEventIdRef.current = nextEvent.event.id;
    // tabIndex=-1 elements still accept a programmatic focus() — the roving
    // stop moving is a render effect of state above, but the actual DOM
    // focus has to move right now, in the same gesture, not on next paint.
    eventHitRefs.current.get(nextEvent.event.id)?.focus();
  }

  function handleEventPointerEnter(id: string) {
    setActive({ kind: 'event', id });
  }

  function handleEventPointerLeave(id: string) {
    setActive((current) =>
      current?.kind === 'event' && current.id === id && focusedEventIdRef.current !== id ? null : current,
    );
  }

  function handleEventFocus(id: string, index: number) {
    focusedEventIdRef.current = id;
    setRovingEventIndex(index);
    setActive({ kind: 'event', id });
  }

  function handleEventBlur(id: string) {
    if (focusedEventIdRef.current === id) focusedEventIdRef.current = null;
    setActive((current) => (current?.kind === 'event' && current.id === id ? null : current));
  }

  function formatWhen(t: number): string {
    // A weekly bucket is named by the Monday it starts on, and a lone date is
    // read as a lone day. Say the whole seven days it stands for.
    if (bucket === 'weekly') {
      const start = new Date(t);
      const end = new Date(t + WEEK_LAST_DAY_MS);
      const label = formatWeekLabel(start.toISOString());
      const span = `${changeSpanFormatter.format(start)}–${weekEndFormatter.format(end)}`;
      return label ? `${label} · ${span}` : span;
    }
    return aggregated
      ? dateFormatter.format(new Date(t))
      : `${dateFormatter.format(new Date(t))}, ${timeFormatter.format(new Date(t))}`;
  }

  // Dates the owner typed for an event, read back in UTC — what they typed
  // is what they see, whatever timezone the browser happens to be in.
  function formatEventDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  function eventDateText(event: LaidOutEvent['event']): string {
    if (!isPeriod(event)) return formatEventDate(event.startsAt);
    return `${formatEventDate(event.startsAt)} – ${formatEventDate(event.endsAt as string)}`;
  }

  function changeForPoint(p: Plotted): WeeklyChange | undefined {
    return changesByWeek.get(p.point.recordedAt);
  }

  function valueText(p: Plotted): string {
    const when = formatWhen(p.t);
    const change = changeForPoint(p);
    const changeSuffix = change ? `, ${formatChangeText(change, unit, precision)}` : '';
    if (aggregated && p.point.min !== undefined && p.point.max !== undefined) {
      return `${when} — ${formatValue(p.point.value, precision)} ${unit} average across ${p.point.count} reading${p.point.count === 1 ? '' : 's'}, ranging ${formatValue(p.point.min, precision)} to ${formatValue(p.point.max, precision)} ${unit}${changeSuffix}`;
    }
    const source = p.point.source ? `, ${SOURCE_LABEL[p.point.source]}` : '';
    return `${when} — ${formatValue(p.point.value, precision)} ${unit}${source}${changeSuffix}`;
  }

  // The measurement block both popovers show: the reading tooltip renders
  // it for the hovered/focused point, the event popover for the reading
  // nearest the event's own visible start. One function so the two can
  // never drift apart.
  function readingInfo(p: Plotted) {
    const change = changeForPoint(p);
    return (
      <>
        <Text as="div" size="2xs" color="muted" className={styles.tooltipTime}>
          {formatWhen(p.t)}
        </Text>
        <Text as="div" family="figure" size="md" className={styles.tooltipValue}>
          {formatValue(p.point.value, precision)} {unit}
        </Text>
        {aggregated && p.point.min !== undefined && p.point.max !== undefined ? (
          <Text as="div" size="2xs" color="muted">
            {p.point.count} reading{p.point.count === 1 ? '' : 's'} · {formatValue(p.point.min, precision)}–{formatValue(p.point.max, precision)} {unit}
          </Text>
        ) : p.point.source ? (
          <Text as="div" size="2xs" color="muted">{SOURCE_LABEL[p.point.source]}</Text>
        ) : null}
        {change && (
          <Text as="div" size="2xs" color="muted">
            {formatChangeText(change, unit, precision)}
          </Text>
        )}
      </>
    );
  }

  // With no data AND no explicit window, there is no axis worth drawing —
  // exactly today's "No readings yet." message. With a `window`, though,
  // the screen has asked for a specific span regardless of what fell inside
  // it, so the plot below still draws that span's own real axis rather than
  // collapsing to this placeholder.
  if (sorted.length === 0 && !window) {
    return (
      <div ref={frameRef} className={styles.frame}>
        <div className={styles.empty}>
          <Text color="muted" size="sm">No readings yet.</Text>
        </div>
      </div>
    );
  }

  const canDrawLine = plotted.length >= 2;
  /**
   * The readings themselves, joined. There is no other line.
   *
   * It used to be a five-point moving average, with the readings as dots
   * beside it — so a weigh-in of 110 was drawn as a dot at 110 and a line at
   * 108, and the chart was quietly showing a number nobody had stood on. The
   * smoothing is gone rather than demoted: a measurement chart draws
   * measurements, and a second, softer line explaining the first is one line
   * more than this asked for.
   */
  const readingPath = canDrawLine ? linearPath(plotted.map((p) => ({ x: p.x, y: p.y }))) : '';

  const areaPath = canDrawLine && readingPath ? `${readingPath} L ${plotted[plotted.length - 1]?.x ?? 0} ${plot.y1} L ${plotted[0]?.x ?? 0} ${plot.y1} Z` : '';
  const activeChange = activePoint ? changeForPoint(activePoint) : undefined;
  // Clamped defensively: `rovingEventIndex` can outlive a shorter
  // `eventsLayout` from a prop change (fewer events, or a resize that drops
  // one off the domain) between one render and the next.
  const effectiveRovingIndex =
    eventsLayout.length > 0 ? Math.min(rovingEventIndex, eventsLayout.length - 1) : 0;
  const eventReadingPoint = activeEvent ? nearestPlottedTo(activeEvent.visibleStartMs) : undefined;

  return (
    <div ref={frameRef} className={styles.frame}>
      <svg
        ref={svgRef}
        className={styles.svg}
        width={width}
        height={svgHeight}
        viewBox={`0 0 ${width} ${svgHeight}`}
        role="img"
        aria-label={
          last
            ? `${formatValue(last.point.value, precision)} ${unit} as of ${dateFormatter.format(new Date(last.t))}${goalThreshold !== undefined ? `, against a ${formatValue(goalThreshold, precision)} ${unit} goal` : ''}.${stripLayout ? ' A band below shows how much changed within each week.' : ''} Use the chart's slider control to read every value.`
            : 'No readings yet.'
        }
      >
        <defs>
          {/* The series is drawn with one reading either side of the window —
              see pointsInWindowWithBleed — so the line crosses both edges of
              the frame instead of starting a little way in. Those two points
              sit outside the plot by definition, and this is what stops them
              being drawn across the y-axis labels. */}
          <clipPath id={plotClipId}>
            <rect x={plot.x0} y={plot.y0} width={Math.max(0, plot.x1 - plot.x0)} height={Math.max(0, plot.y1 - plot.y0)} />
          </clipPath>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className={styles.areaStopStart} />
            <stop offset="100%" className={styles.areaStopEnd} />
          </linearGradient>
        </defs>

        {/* Gridlines: solid hairlines, one shade off the surface. Dashed is
            reserved for the goal threshold below. */}
        <g aria-hidden="true">
          {valueTicks.map((tick) => {
            const y = yScale(tick);
            return (
              <g key={tick}>
                <line className={styles.gridline} x1={plot.x0} x2={plot.x1} y1={y} y2={y} />
                <text className={styles.tickLabel} x={plot.x0 - 8} y={y + 4} textAnchor="end">
                  {formatTickValue(tick, tickStep)}
                </text>
              </g>
            );
          })}
        </g>

        {/* x-axis labels: a handful of evenly spaced points in time, not one
            per reading — the readings are unevenly spaced by design. */}
        <g aria-hidden="true">
          {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
            const t = minT + spanMs * fraction;
            const x = plot.x0 + fraction * (plot.x1 - plot.x0);
            return (
              <text key={fraction} className={styles.xTickLabel} x={x} y={axisBottomY + 20} textAnchor={fraction === 0 ? 'start' : fraction === 1 ? 'end' : 'middle'}>
                {formatAxisTick(t, spanMs)}
              </text>
            );
          })}
        </g>

        {/* Life events, laid over the chart BEHIND the series — emitted here,
            right after the gridlines, and before the area fill, the dots,
            the reading line and the endpoint label, so "does not obscure the
            line" is a structural fact rather than a hope. A moment is a
            thin vertical line; a period is a low-opacity band, both on the
            SAME x scale the series uses. Neither reserves a status colour —
            an event is context, never a goal's state. */}
        {eventsLayout.length > 0 && (
          <>
            <g aria-hidden="true">
              {eventsLayout.map((laidOut) => {
                // The active event's own band/line is more prominent — still
                // a neutral/structural token, never a reserved --status-*
                // one (that vocabulary means a goal's state, not "this is
                // the one the reader is looking at").
                const isActive = active?.kind === 'event' && active.id === laidOut.event.id;
                return laidOut.kind === 'period' ? (
                  <rect
                    key={`${laidOut.event.id}-band`}
                    className={styles.eventBand}
                    data-active={isActive ? 'true' : undefined}
                    x={laidOut.x0}
                    y={plot.y0}
                    width={Math.max(0, laidOut.x1 - laidOut.x0)}
                    height={Math.max(0, plot.y1 - plot.y0)}
                  />
                ) : (
                  <line
                    key={`${laidOut.event.id}-line`}
                    className={styles.eventLine}
                    data-active={isActive ? 'true' : undefined}
                    x1={laidOut.x}
                    x2={laidOut.x}
                    y1={plot.y0}
                    y2={plot.y1}
                  />
                );
              })}
            </g>
            {/* No title is drawn inside the plot: every label lives in its
                own lane above it, one stacked row per collision, truncated
                to fit. A category `matchKnownCategory` recognises draws a
                small neutral icon before its title; anything else draws a
                generic mark — no colour, so the marks never compete with
                the measurement line or borrow a hue the accent/status/
                identity scales already own. The full title, the date, and
                the reading beside it live in the popover a hover or a focus
                opens (rendered once, after the SVG, alongside the reading
                tooltip it shares a placement strategy with) — not in an SVG
                `<title>`, which would show a second, differently-styled
                tooltip on top of it. The visible text is `aria-hidden`
                because the hit rect below carries the same words as its own
                accessible name. */}
            <g>
              {eventsLayout.map((laidOut, i) => {
                const rowTop = MARGIN.top + laidOut.hitY0;
                const iconCx = laidOut.labelX + EVENT_ICON_GLYPH_SIZE / 2;
                const iconCy = rowTop + eventRowHeightPx / 2;
                const isRoving = i === effectiveRovingIndex;
                const isActiveEvent = active?.kind === 'event' && active.id === laidOut.event.id;

                return (
                  <g key={laidOut.event.id}>
                    <EventIcon category={laidOut.event.category} cx={iconCx} cy={iconCy} />
                    <text
                      aria-hidden="true"
                      className={styles.eventLabel}
                      x={laidOut.labelX + EVENT_ICON_WIDTH_PX}
                      y={eventLabelBaselineY(laidOut.labelRow)}
                    >
                      {laidOut.labelText}
                    </text>
                    {/* The lane's one tab stop: a roving tabindex, not one
                        stop per event — see the keydown handler's own
                        comment. Sized (and, under a coarse pointer, heightened
                        to 44px) from `eventLayout`'s own hitX0/hitX1/hitY0/hitY1,
                        never recomputed here, and never reaching down into
                        the plot's own hitLayer rect below. */}
                    <rect
                      ref={(el) => {
                        if (el) eventHitRefs.current.set(laidOut.event.id, el);
                        else eventHitRefs.current.delete(laidOut.event.id);
                      }}
                      className={styles.eventHit}
                      x={laidOut.hitX0}
                      y={rowTop}
                      width={Math.max(0, laidOut.hitX1 - laidOut.hitX0)}
                      height={Math.max(0, laidOut.hitY1 - laidOut.hitY0)}
                      tabIndex={isRoving ? 0 : -1}
                      role="button"
                      aria-label={laidOut.event.title}
                      aria-expanded={isActiveEvent}
                      aria-describedby={isActiveEvent ? popoverId : undefined}
                      onPointerEnter={() => handleEventPointerEnter(laidOut.event.id)}
                      onPointerLeave={() => handleEventPointerLeave(laidOut.event.id)}
                      onFocus={() => handleEventFocus(laidOut.event.id, i)}
                      onBlur={() => handleEventBlur(laidOut.event.id)}
                      onKeyDown={(keyEvent) => handleEventKeyDown(keyEvent, i)}
                    />
                  </g>
                );
              })}
            </g>
          </>
        )}

        <g clipPath={`url(#${plotClipId})`}>
        {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" aria-hidden="true" />}

        {/* Aggregated buckets show the spread they conceal instead of being
            drawn as if each were a raw reading. */}
        {aggregated && (
          <g aria-hidden="true">
            {plotted.map((p, i) =>
              p.yMin !== undefined && p.yMax !== undefined ? (
                <line key={i} className={styles.spreadLine} x1={p.x} x2={p.x} y1={p.yMin} y2={p.yMax} />
              ) : null,
            )}
            {plotted.map((p, i) => (
              <circle key={i} className={styles.bucketMarker} cx={p.x} cy={p.y} r={3} />
            ))}
          </g>
        )}

        {/* Raw readings, on the line rather than beside it: the line joins
            these exact values, so a dot marks where a segment turns. Never
            drawn for aggregated buckets — an average must not look like
            something someone actually measured. */}
        {!aggregated && (
          <g aria-hidden="true">
            {plotted.map((p, i) => (
              <circle key={i} className={styles.rawDot} cx={p.x} cy={p.y} r={2.5} />
            ))}
          </g>
        )}

        {readingPath && <path className={styles.readingLine} d={readingPath} aria-hidden="true" />}
        </g>

        {goalThreshold !== undefined && goalOnAxis && (
          <g aria-hidden="true">
            <line className={styles.goalLine} x1={plot.x0} x2={plot.x1} y1={yScale(goalThreshold)} y2={yScale(goalThreshold)} />
            <text className={styles.goalLabel} x={plot.x0 + 4} y={yScale(goalThreshold) - 6}>
              Goal {formatValue(goalThreshold, precision)} {unit}
            </text>
          </g>
        )}

        {/* The goal lies beyond this axis on purpose — drawing it to scale
            would have squashed the readings into a straight line. It still
            has to be visible, so it sits on the edge it lies past, saying how
            far there is to go, and becomes a real line again once the
            readings come near enough that both fit. */}
        {goalThreshold !== undefined && !goalOnAxis && last && (
          <g aria-hidden="true">
            {(() => {
              const below = goalThreshold < last.point.value;
              const edgeY = below ? plot.y1 : plot.y0;
              const distance = Math.abs(last.point.value - goalThreshold);
              return (
                <>
                  <line className={styles.goalEdge} x1={plot.x0} x2={plot.x1} y1={edgeY} y2={edgeY} />
                  <text className={styles.goalLabel} x={plot.x0 + 4} y={below ? edgeY - 6 : edgeY + 14}>
                    {`Goal ${formatValue(goalThreshold, precision)} ${unit} — ${formatValue(distance, precision)} ${unit} to go`}
                  </text>
                </>
              );
            })()}
          </g>
        )}

        {/* The one value worth labelling directly on the plot besides the
            goal: the latest reading. */}
        {last && (
          <g aria-hidden="true">
            <circle className={styles.endpointDot} cx={last.x} cy={last.y} r={4.5} />
            <text className={styles.endpointLabel} x={Math.min(last.x + 10, width - 6)} y={last.y + 4} textAnchor={last.x + 10 > width - 40 ? 'end' : 'start'}>
              {formatValue(last.point.value, precision)}
            </text>
          </g>
        )}

        {/* The change strip: its own band, its own zero baseline, its own
            scale — NEVER the main yScale. A level in `unit` and a change in
            `unit` are different measures; one vertical axis for both is the
            single worst thing a chart can do. Bar colour never judges: ONE
            accent token whatever the sign, because weight (like most nodes)
            is 'neutral' in the catalog — losing is not inherently better
            than gaining, and the chart must not contradict that by painting
            one side of zero differently from the other. Which side of the
            baseline a bar grows from is what says gained or lost. */}
        {stripLayout && (
          <g aria-hidden="true">
            <text className={styles.stripLabel} x={plot.x0} y={stripLayout.stripY0 - 6}>
              {unit} change per week
            </text>
            <line
              className={styles.stripZeroLine}
              x1={plot.x0}
              x2={plot.x1}
              y1={stripLayout.zeroY}
              y2={stripLayout.zeroY}
            />
            {stripLayout.bars.map((bar) => {
              const isActive = activeChange?.weekStart === bar.change.weekStart;
              const d = changeBarPath(bar.x, bar.barTop, bar.width, bar.barHeight, bar.roundTop);
              return d ? (
                <path
                  key={bar.change.weekStart}
                  d={d}
                  className={styles.changeBar}
                  data-active={isActive ? 'true' : undefined}
                />
              ) : null;
            })}
          </g>
        )}

        {activePoint && (
          <g aria-hidden="true">
            <line
              className={styles.crosshairLine}
              x1={activePoint.x}
              x2={activePoint.x}
              y1={plot.y0}
              y2={stripLayout ? stripLayout.stripY1 : plot.y1}
            />
            <circle className={styles.hoverDot} cx={activePoint.x} cy={activePoint.y} r={5} />
          </g>
        )}

        {/* The hit layer: a generous nearest-point target rather than a
            demand to land on a 2.5px dot, and the one focusable control that
            exposes every value — hover enhances, it never gates. Extended
            down through the change strip (when present) so hovering a bar
            reaches the same nearest-point lookup as hovering the line above
            it; nearestIndexForClientX only ever compares x, so the extra
            height changes nothing about which point wins. Because a bar's
            drawn width is capped at pixelsPerWeek minus a gap while this
            hit region's x-distance contest divides the FULL pixelsPerWeek
            between neighbours, the hit target a bar gets is always at least
            as wide as the bar itself, usually wider. */}
        <rect
          x={plot.x0}
          y={plot.y0}
          width={Math.max(0, plot.x1 - plot.x0)}
          height={Math.max(0, (stripLayout ? stripLayout.stripY1 : plot.y1) - plot.y0)}
          className={styles.hitLayer}
          tabIndex={0}
          role="slider"
          aria-label={`${unit} readings over time — use the left and right arrow keys to move between them`}
          aria-valuemin={0}
          aria-valuemax={Math.max(0, plotted.length - 1)}
          aria-valuenow={activeReadingIndex ?? Math.max(0, plotted.length - 1)}
          aria-valuetext={activePoint ? valueText(activePoint) : last ? valueText(last) : undefined}
          onPointerMove={handlePointerMove}
          onPointerDown={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onFocus={handleReadingFocus}
          onBlur={handleReadingBlur}
          onKeyDown={handleKeyDown}
        />
      </svg>

      {/* Never both: `active` names exactly one of a reading or an event,
          so only one of the two divs below can ever be present at once. */}
      {activePoint && (
        <div
          className={styles.tooltip}
          style={{
            left: `${(activePoint.x / width) * 100}%`,
            top: `${(activePoint.y / svgHeight) * 100}%`,
          }}
          data-align={activePoint.x > width * 0.75 ? 'end' : activePoint.x < width * 0.25 ? 'start' : 'center'}
          /* Opens downward when the reading sits too near the top of the plot
             to rise from. The chart lives in a horizontal scroll container,
             and a scroll container clips BOTH axes — `overflow-x: auto` makes
             the other axis clip too — so a tooltip that rose out of the chart
             lost its top edge rather than overflowing harmlessly. Measured at
             22px of it, over a sweep of the whole plot. */
          data-flip={
            activePoint.y - plot.y0 < TOOLTIP_FLIP_HEADROOM &&
            plot.y1 - activePoint.y > activePoint.y - plot.y0
              ? 'down'
              : undefined
          }
        >
          {readingInfo(activePoint)}
        </div>
      )}

      {/* The event popover: same placement STRATEGY as the reading tooltip
          above (an absolutely-positioned div, percentage of the viewBox,
          the same data-align edge handling) but anchored to the label's own
          geometry from `layoutEvents` — its labelX and its row — never to
          `activePoint`, which is a reading's coordinates and means nothing
          for an event. */}
      {activeEvent && (
        <div
          id={popoverId}
          className={styles.eventPopover}
          style={{ top: `${(eventLabelBaselineY(activeEvent.labelRow) / svgHeight) * 100}%` }}
          /* Pinned to whichever SIDE the event is not on, rather than anchored
             to the event itself. Anchored, it sat squarely on top of the very
             band it was describing, which is the one thing the reader was
             looking at. The connection is carried by the highlight instead:
             the mark lights up at the same moment this appears. */
          data-side={activeEvent.labelX > width / 2 ? 'start' : 'end'}
        >
          <Text as="div" size="sm" className={styles.eventPopoverTitle}>
            {activeEvent.event.title}
          </Text>
          <Text as="div" size="2xs" color="muted">
            {eventDateText(activeEvent.event)}
          </Text>
          {activeEvent.event.category && (
            <Text as="div" size="2xs" color="muted">
              {activeEvent.event.category}
            </Text>
          )}
          {activeEvent.event.note && (
            <Text as="div" size="2xs" color="muted" className={styles.eventPopoverNote}>
              {activeEvent.event.note}
            </Text>
          )}
          {eventReadingPoint && (
            <>
              {/* Named, because two date lines in different formats sit
                  together here otherwise: the event's own days, written in
                  UTC so they read as the owner typed them, and directly
                  under it a reading taken at a real instant shown in local
                  time. Without this line the second one just looks like the
                  first one gone wrong. */}
              <Text as="div" size="2xs" color="muted" className={styles.eventPopoverDivider}>
                Nearest reading
              </Text>
              {readingInfo(eventReadingPoint)}
            </>
          )}
        </div>
      )}

      {plotted.length === 1 && (
        <Text as="div" size="xs" color="muted" className={styles.caption}>
          Only one reading yet — a line needs at least two.
        </Text>
      )}
    </div>
  );
}
